/* eslint-disable unicorn/no-null, sonarjs/no-unused-vars */

import { create } from 'zustand';
import { editor } from 'monaco-editor';

export type TOpenFile = {
    content: string;
    dirty: boolean;

    baseGeneration: string | null;
};

type TDesignerFilesStore = {
    selectedVersionId: string | null;
    setSelectedVersionId: (id: string | null) => void;

    openFiles: Partial<Record<string, TOpenFile>>;
    openFileOrder: string[];
    activeFilePath: string | null;

    pendingDeletePaths: Set<string>;

    openFile: (
        path: string,
        content: string,
        baseGeneration?: string | null
    ) => void;
    closeFile: (path: string) => void;
    setFileContent: (path: string, content: string) => void;
    markDirty: (path: string, dirty: boolean) => void;
    setBaseGeneration: (path: string, baseGeneration: string | null) => void;
    setActiveFile: (path: string | null) => void;
    clearOpenFiles: () => void;
    addPendingDelete: (path: string) => void;
    removePendingDelete: (path: string) => void;
    flush: () => void;
};

const initialState: Pick<
    TDesignerFilesStore,
    | 'selectedVersionId'
    | 'openFiles'
    | 'openFileOrder'
    | 'activeFilePath'
    | 'pendingDeletePaths'
> = {
    selectedVersionId: null,
    openFiles: {},
    openFileOrder: [],
    activeFilePath: null,
    pendingDeletePaths: new Set<string>()
};

const useDesignerFilesStore = create<TDesignerFilesStore>()((set) => ({
    ...initialState,

    setSelectedVersionId: (id) => {
        if (id) {
            const prefix = `${id}/`;
            for (const model of editor.getModels()) {
                const uriPath = model.uri.path;
                const normalized = uriPath.startsWith('/')
                    ? uriPath.slice(1)
                    : uriPath;
                if (
                    normalized.includes('/') &&
                    !normalized.startsWith(prefix)
                ) {
                    model.dispose();
                }
            }
        }
        set({ selectedVersionId: id });
    },

    openFile: (path, content, baseGeneration = null) => {
        set((state) => ({
            openFiles: {
                ...state.openFiles,
                [path]: state.openFiles[path] ?? {
                    content,
                    dirty: false,
                    baseGeneration
                }
            },
            openFileOrder: state.openFileOrder.includes(path)
                ? state.openFileOrder
                : [...state.openFileOrder, path],
            activeFilePath: path
        }));
    },

    closeFile: (path) => {
        set((state) => {
            const { [path]: _removed, ...rest } = state.openFiles;
            const newOrder = state.openFileOrder.filter((p) => p !== path);
            const newActive =
                state.activeFilePath === path
                    ? (newOrder.at(-1) ?? null)
                    : state.activeFilePath;
            return {
                openFiles: rest,
                openFileOrder: newOrder,
                activeFilePath: newActive
            };
        });
    },

    setFileContent: (path, content) => {
        set((state) => {
            if (!state.openFiles[path]) {
                return state;
            }
            return {
                openFiles: {
                    ...state.openFiles,
                    [path]: { ...state.openFiles[path], content }
                }
            };
        });
    },

    markDirty: (path, dirty) => {
        set((state) => {
            if (!state.openFiles[path]) {
                return state;
            }
            return {
                openFiles: {
                    ...state.openFiles,
                    [path]: { ...state.openFiles[path], dirty }
                }
            };
        });
    },

    setBaseGeneration: (path, baseGeneration) => {
        set((state) => {
            if (!state.openFiles[path]) {
                return state;
            }
            return {
                openFiles: {
                    ...state.openFiles,
                    [path]: {
                        ...state.openFiles[path],
                        baseGeneration
                    }
                }
            };
        });
    },

    setActiveFile: (path) => {
        set({ activeFilePath: path });
    },

    clearOpenFiles: () => {
        set({ openFiles: {}, openFileOrder: [], activeFilePath: null });
    },

    addPendingDelete: (path) => {
        set((state) => {
            const next = new Set(state.pendingDeletePaths);
            next.add(path);
            return { pendingDeletePaths: next };
        });
    },

    removePendingDelete: (path) => {
        set((state) => {
            const next = new Set(state.pendingDeletePaths);
            next.delete(path);
            return { pendingDeletePaths: next };
        });
    },

    flush: () => {
        set({
            ...initialState,

            pendingDeletePaths: new Set<string>()
        });
    }
}));

export default useDesignerFilesStore;
